import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  Grid,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Save,
  CheckCircle,
  Error
} from '@mui/icons-material';
import api from '../../../services/api';

function ProviderAPIConfiguration({ token, configuration, onConfigChange }) {
  const [apiKeys, setApiKeys] = useState({
    elevenlabs: '',
    google: '',
    azure: '',
    openai: ''
  });
  const [showApiKey, setShowApiKey] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);
  const [testStatus, setTestStatus] = useState({});

  useEffect(() => {
    // Load existing API configuration
    if (configuration?.providers) {
      setApiKeys({
        elevenlabs: configuration.providers.elevenlabs?.apiKey || '',
        google: configuration.providers.google?.keyFilename || '',
        azure: configuration.providers.azure?.subscriptionKey || '',
        openai: configuration.providers.openai?.apiKey || ''
      });
    }
  }, [configuration]);

  const handleApiKeyChange = (provider, value) => {
    setApiKeys(prev => ({
      ...prev,
      [provider]: value
    }));
  };

  const handleTestConnection = async (provider) => {
    try {
      setTestStatus(prev => ({ ...prev, [provider]: 'testing' }));
      
      // Test the provider connection
      const response = await api.post('/tts/test-connection', {
        provider,
        apiKey: apiKeys[provider]
      }, token);

      if (response.data?.success) {
        setTestStatus(prev => ({ ...prev, [provider]: 'success' }));
        setTimeout(() => {
          setTestStatus(prev => ({ ...prev, [provider]: null }));
        }, 3000);
      } else {
        setTestStatus(prev => ({ ...prev, [provider]: 'error' }));
      }
    } catch (error) {
      console.error(`Failed to test ${provider} connection:`, error);
      setTestStatus(prev => ({ ...prev, [provider]: 'error' }));
    }
  };

  const handleSave = async () => {
    try {
      const updatedConfig = {
        ...configuration,
        providers: {
          ...configuration.providers,
          elevenlabs: {
            ...configuration.providers.elevenlabs,
            apiKey: apiKeys.elevenlabs
          },
          google: {
            ...configuration.providers.google,
            keyFilename: apiKeys.google
          },
          azure: {
            ...configuration.providers.azure,
            subscriptionKey: apiKeys.azure
          },
          openai: {
            ...configuration.providers.openai,
            apiKey: apiKeys.openai
          }
        }
      };

      const response = await api.put('/tts/configuration', updatedConfig, token);
      
      if (response.data?.success) {
        setSaveStatus({ type: 'success', message: 'API configuration saved successfully' });
        if (onConfigChange) {
          onConfigChange(updatedConfig);
        }
      }
    } catch (error) {
      console.error('Failed to save API configuration:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save API configuration' });
    }
  };

  const providerConfigs = [
    {
      key: 'elevenlabs',
      name: 'ElevenLabs',
      description: 'High-quality voice synthesis with emotional range',
      placeholder: 'sk_...',
      helperText: 'Your ElevenLabs API key from the dashboard',
      cost: '$0.18/1k chars'
    },
    {
      key: 'google',
      name: 'Google Cloud TTS',
      description: 'Cost-effective voice synthesis with wide language support',
      placeholder: '/path/to/service-account.json',
      helperText: 'Path to Google Cloud service account JSON file',
      cost: '$0.016/1k chars'
    },
    {
      key: 'azure',
      name: 'Azure Cognitive Services',
      description: 'Microsoft\'s neural voice synthesis',
      placeholder: 'Your subscription key',
      helperText: 'Azure Cognitive Services Speech API key',
      cost: '$0.016/1k chars'
    },
    {
      key: 'openai',
      name: 'OpenAI TTS',
      description: 'Simple and reliable text-to-speech',
      placeholder: 'sk-...',
      helperText: 'Your OpenAI API key',
      cost: '$0.015/1k chars'
    }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Provider API Configuration
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Configure API credentials for each TTS provider. These credentials are stored securely and never exposed to clients.
      </Typography>

      <Grid container spacing={3}>
        {providerConfigs.map(provider => (
          <Grid item xs={12} md={6} key={provider.key}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="subtitle1">{provider.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {provider.description}
                    </Typography>
                    <Chip 
                      label={provider.cost} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  {testStatus[provider.key] === 'success' && (
                    <CheckCircle color="success" />
                  )}
                  {testStatus[provider.key] === 'error' && (
                    <Error color="error" />
                  )}
                </Box>

                <TextField
                  fullWidth
                  label={`${provider.name} Credentials`}
                  type={showApiKey[provider.key] ? 'text' : 'password'}
                  value={apiKeys[provider.key]}
                  onChange={(e) => handleApiKeyChange(provider.key, e.target.value)}
                  placeholder={provider.placeholder}
                  helperText={provider.helperText}
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowApiKey(prev => ({
                            ...prev,
                            [provider.key]: !prev[provider.key]
                          }))}
                          edge="end"
                        >
                          {showApiKey[provider.key] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleTestConnection(provider.key)}
                  disabled={!apiKeys[provider.key] || testStatus[provider.key] === 'testing'}
                  sx={{ mt: 1 }}
                >
                  {testStatus[provider.key] === 'testing' ? 'Testing...' : 'Test Connection'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={<Save />}
          onClick={handleSave}
          size="large"
        >
          Save API Configuration
        </Button>
      </Box>

      {saveStatus && (
        <Alert 
          severity={saveStatus.type} 
          sx={{ mt: 2 }}
          onClose={() => setSaveStatus(null)}
        >
          {saveStatus.message}
        </Alert>
      )}

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Security Note
        </Typography>
        <Typography variant="body2">
          API keys are encrypted and stored securely on the server. They are never sent to client browsers or exposed in API responses.
          Make sure to keep your API keys confidential and rotate them regularly.
        </Typography>
      </Alert>
    </Box>
  );
}

export default ProviderAPIConfiguration;